import React, { useState, useEffect } from 'react';
import InputField from '../components/InputField';
import OutputCard from '../components/OutputCard';

const BedEqd2Calculator = () => {
    const [inputs, setInputs] = useState({
        totalDose: '',
        numFractions: '',
        alphaBetaRatio: '10',
    });
    const [outputs, setOutputs] = useState({ bed: '-', eqd2: '-' });

    const handleInputChange = (field, value) => {
        setInputs(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        const totalDose = parseFloat(inputs.totalDose);
        const numFractions = parseInt(inputs.numFractions, 10);
        const alphaBeta = parseFloat(inputs.alphaBetaRatio);

        if (totalDose > 0 && numFractions > 0 && alphaBeta > 0) {
            const dosePerFraction = totalDose / numFractions;
            const bed = totalDose * (1 + (dosePerFraction / alphaBeta));
            const eqd2 = totalDose * ((dosePerFraction + alphaBeta) / (2 + alphaBeta));
            setOutputs({
                bed: bed.toFixed(2),
                eqd2: eqd2.toFixed(2),
            });
        } else {
            setOutputs({ bed: '-', eqd2: '-' });
        }
    }, [inputs]);

    return (
        <div>
            <div className="text-center mb-8">
                <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-cyan-400 mb-2">BED and EQD2 Calculator</h1>
                <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg">Calculate the Biological Effective Dose (BED) and Equivalent Dose in 2 Gy fractions (EQD2).</p>
            </div>
            
            <div className="p-4 md:p-8 bg-gray-100 dark:bg-gray-800 rounded-xl mb-8 border border-gray-300 dark:border-gray-700 shadow-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <InputField
                        label="Total Dose (Gy)"
                        value={inputs.totalDose}
                        onChange={e => handleInputChange('totalDose', e.target.value)}
                        placeholder="e.g., 60"
                        step="0.1"
                    />
                    <InputField
                        label="Number of Fractions"
                        value={inputs.numFractions}
                        onChange={e => handleInputChange('numFractions', e.target.value)}
                        placeholder="e.g., 30"
                        step="1"
                    />
                    {/* --- MODIFICATION: Corrected the label text to fix syntax error --- */}
                    <InputField
                        label="Alpha/Beta (α/β) Ratio"
                        value={inputs.alphaBetaRatio}
                        onChange={e => handleInputChange('alphaBetaRatio', e.target.value)}
                        placeholder="e.g., 10"
                        step="0.1"
                    />
                </div>
            </div>
            
            <div className="mt-8 p-4 md:p-6 bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-300 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-center text-gray-800 dark:text-gray-200 mb-4">Common α/β Ratios</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <div>
                        <h4 className="font-bold text-center md:text-left text-gray-700 dark:text-gray-300 mb-2">Tumors / Early Responding Tissues</h4>
                        <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 text-sm">
                            <li><strong>Most Carcinomas (Lung, H&N, etc):</strong> 10 Gy</li>
                            <li><strong>Glioblastoma:</strong> 10 Gy</li>
                            <li><strong>Sarcoma:</strong> 4 Gy</li>
                            <li><strong>Breast Cancer:</strong> 4 Gy</li>
                            <li><strong>Melanoma:</strong> 2.5 Gy</li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-bold text-center md:text-left text-gray-700 dark:text-gray-300 mb-2">Late Responding Normal Tissues</h4>
                        <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 text-sm">
                            <li><strong>Prostate Cancer:</strong> 1.5 - 3 Gy</li>
                            <li><strong>Spinal Cord / Brain:</strong> 2 Gy</li>
                            <li><strong>Lung:</strong> 3 Gy</li>
                            <li><strong>Kidney / Bladder:</strong> 3 Gy</li>
                            <li><strong>Rectum:</strong> 3-5 Gy</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <OutputCard label="Biological Effective Dose (BED)" value={outputs.bed} colour="purple" />
                <OutputCard label="Equivalent Dose in 2Gy Fractions (EQD2)" value={outputs.eqd2} colour="indigo" />
            </div>
        </div>
    );
};

export default BedEqd2Calculator;